/*
===============================================================
  ____    ___    ____   _   __       __                
 |  _ |  / _ \  |  _ \ | | |   \    |   \  ___  __ __  
 |  __| | |_| | |  __/ | | | |) | o | |) |/ -_) \ V /  
 |_|\_\ |_| |_| |_|    |_| |___/    |___/ \___|  \_/    o o o   

===============================================================
*/

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import TableHeader from "components/Table/TableHeader";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Navbar from "examples/Navbars/NavBar";
import Footer from "examples/Footer";
import { useTranslation } from "react-i18next";
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import { useSelector } from "react-redux";
import * as Func from "services/Function";
import MenuCollap from "./MenuCollap";

const initAttr = {
  main_menu_id: "",
  key_id: "",
  main_id: "",
  main_menu_name: "",
  main_menu_name_en: "",
  enable: 1,
  weight: "",
  created_at: "",
  updated_at: "",
};

const initField = {
  main_menu_id: "",
  menu_id: "",
  key_id: "",
  main_id: "",
  menu_name: "",
  menu_name_en: "",
  enable: 1,
  weight: "",
  created_at: "",
  updated_at: "",
};

const initFind = {
  search: "",
  order_by: "weight",
  order_sort: "asc",
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

function MenuMain() {
  const mod = "mainmenu";
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [field, setField] = useState(initField);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [sub, setSub] = useState(false);
  const [menuActive, setMenuActive] = useState(0);

  const initHeader = [
    { id: "collap", label: "", align: "center", sort: false },
    { id: "main_menu_id", label: t(`attr.code`), align: "center" },
    { id: "key_id", label: t(`attr.id`), align: "left" },
    { id: "main_menu_name", label: t(`attr.name_th`), align: "left" },
    { id: "main_menu_name_en", label: t(`attr.name_en`), align: "left" },
    { id: "weight", label: t(`attr.sort`), align: "center" },
    { id: "enable", label: t(`attr.enable`), align: "center" },
    { id: "created_at", label: t(`attr.date_create`), align: "center" },
    { id: "updated_at", label: t(`attr.date_update`), align: "center" },
    { id: "edit", label: "", align: "center", sort: false },
    { id: "delete", label: "", align: "center", sort: false },
  ];

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon="notifications"
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData("POST", `/app/req/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      sort: data.order_sort,
    }).then((result) => {
      if (result.status === "OK") {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const openSub = (item) => {
    setLoading(true);
    setField(initField);

    FetchData("GET", `/app/req/menu/${item.menu_id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "OK") {
        setField(result.data);
        setModalEdit(true);

        setLoading(false);
      }
    });
  };

  const addSub = () => {
    Swal.fire({
      title: t(`alert.confirm_add_title`),
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#3085d6",
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: "#dc3545",
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        setModalAdd(false);

        FetchData("PUT", `/app/req/menu`, app.token, {
          main_menu_id: field.main_menu_id,
          key_id: field.key_id,
          menu_name: field.menu_name,
          menu_name_en: field.menu_name_en,
          weight: field.weight,
          enable: field.enable,
        }).then((result) => {
          setLoading(false);
          setMenuActive(field.main_menu_id);

          if (result.status === "OK") {
            view(find);
            showNotify(t(`alert.addcomplete`), "success");
          } else {
            showNotify(t(`alert.saveerror`), "error");
          }
        });
      }
    });
  };

  const editSub = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#3085d6",
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: "#dc3545",
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        setModalEdit(false);

        FetchData("PATCH", `/app/req/menu/${field.menu_id}`, app.token, {
          key_id: field.key_id,
          menu_name: field.menu_name,
          menu_name_en: field.menu_name_en,
          weight: field.weight,
          enable: field.enable,
        }).then((result) => {
          setLoading(false);
          setMenuActive(field.menu_id);

          if (result.status === "OK") {
            view(find);
            showNotify(t(`alert.editcomplete`), "success");
          } else {
            setModalEdit(true);
            showNotify(t(`alert.saveerror`), "error");
          }
        });
      }
    });
  };

  const delSub = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#3085d6",
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: "#dc3545",
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        FetchData("DELETE", `/app/req/menu/${item.menu_id}`, app.token).then((result) => {
          setLoading(false);
          setMenuActive(item.main_menu_id);

          if (result.status === "OK") {
            view(find);
            showNotify(t(`alert.deletecomplete`), "success");
          } else {
            showNotify(t(`alert.deleteerror`), "error");
          }
        });
      }
    });
  };

  const open = (item) => {
    setLoading(true);
    setAttr(initAttr);

    FetchData("GET", `/app/req/${mod}/${item.main_menu_id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "OK") {
        setAttr({ ...result.data });
        setModalEdit(true);

        setLoading(false);
      }
    });
  };

  const add = () => {
    Swal.fire({
      title: t(`alert.confirm_add_title`),
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#3085d6",
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: "#dc3545",
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        setModalAdd(false);

        FetchData("PUT", `/app/req/${mod}`, app.token, {
          key_id: attr.key_id,
          main_menu_name: attr.main_menu_name,
          main_menu_name_en: attr.main_menu_name_en,
          weight: attr.weight,
          enable: attr.enable,
        }).then((result) => {
          setLoading(false);

          if (result.status === "OK") {
            view(find);
            showNotify(t(`alert.addcomplete`), "success");
          } else {
            setModalAdd(true);
            showNotify(t(`alert.saveerror`), "error");
          }
        });
      }
    });
  };

  const edit = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#3085d6",
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: "#dc3545",
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        setModalEdit(false);

        FetchData("PATCH", `/app/req/${mod}/${attr.main_menu_id}`, app.token, {
          key_id: attr.key_id,
          main_menu_name: attr.main_menu_name,
          main_menu_name_en: attr.main_menu_name_en,
          weight: attr.weight,
          enable: attr.enable,
        }).then((result) => {
          setLoading(false);

          if (result.status === "OK") {
            view(find);
            showNotify(t(`alert.editcomplete`), "success");
          } else {
            setModalEdit(true);
            showNotify(t(`alert.saveerror`), "error");
          }
        });
      }
    });
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#3085d6",
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: "#dc3545",
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        FetchData("DELETE", `/app/req/${mod}/${item.main_menu_id}`, app.token).then((result) => {
          setLoading(false);

          if (result.status === "OK") {
            view(find);
            showNotify(t(`alert.deletecomplete`), "success");
          } else {
            showNotify(t(`alert.deleteerror`), "error");
          }
        });
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? "desc" : "asc" });
  };

  const init = () => {
    FetchData("GET", `/app/req/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "OK") {
        setMenuActive(0);
        setAttr(initAttr);
        setField(initField);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const btnAdd = (main_menu_id) => {
    return (
      <MDButton
        color="info"
        size="small"
        variant="outlined"
        onClick={() => {
          setSub(true);
          setField({ ...initField, main_menu_id: main_menu_id });
          setModalAdd(true);
        }}
      >
        <Icon>add</Icon> {t(`element.add`)}
      </MDButton>
    );
  };

  const btnEdit = (item, submenu = false) => {
    return (
      <MDButton
        variant="text"
        color="success"
        size="small"
        onClick={() => {
          if (submenu) {
            openSub(item);
            setSub(true);
          } else {
            open(item);
            setSub(false);
          }
        }}
      >
        <Icon>border_color</Icon> {t(`element.edit`)}
      </MDButton>
    );
  };

  const btnDel = (item, submenu = false) => {
    return (
      <MDButton
        variant="text"
        color="error"
        size="small"
        onClick={() => {
          if (submenu) delSub(item);
          else del(item);
        }}
      >
        <Icon>delete</Icon> {t(`element.delete`)}
      </MDButton>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <form
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    view({ ...find, page: 1 });
                  }}
                >
                  <MDInput
                    label={t(`element.search`)}
                    onChange={(e) => setFind({ ...find, search: e.target.value })}
                    value={find.search}
                  />
                </form>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={() => {
                    setSub(false);
                    setAttr(initAttr);
                    setModalAdd(true);
                  }}
                >
                  <Icon>add_circle_outline</Icon>&nbsp;{t(`element.add`)}
                </MDButton>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table aria-label="collapsible table">
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <MenuCollap
                      key={row.main_menu_id}
                      row={row}
                      btnAdd={btnAdd}
                      btnEdit={btnEdit}
                      btnDel={btnDel}
                      menuActive={menuActive}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <MDButton variant={show ? "contained" : "outlined"} color="dark" onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant="outlined"
                  color="secondary"
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label={t(`attr.id`)}
            onChange={(e) => setAttr({ ...attr, key_id: e.target.value })}
            value={attr.key_id}
            variant="standard"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={t(`attr.name_th`)}
            onChange={(e) => setAttr({ ...attr, main_menu_name: e.target.value })}
            value={attr.main_menu_name}
            variant="standard"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={t(`attr.name_en`)}
            onChange={(e) => setAttr({ ...attr, main_menu_name_en: e.target.value })}
            value={attr.main_menu_name_en}
            variant="standard"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={t(`attr.sort`)}
            onChange={(e) => setAttr({ ...attr, weight: e.target.value })}
            value={attr.weight}
            variant="standard"
            fullWidth
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={attr.enable === 1}
                onChange={(e) => setAttr({ ...attr, enable: e.target.checked ? 1 : 0 })}
                inputProps={{
                  "aria-label": "enable",
                }}
              />
            }
            label="Enable"
          />
        </Grid>
      </Grid>
    );
  };

  const renderInfoSub = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label={t(`attr.id`)}
            onChange={(e) => setField({ ...field, key_id: e.target.value })}
            value={field.key_id}
            variant="standard"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={t(`attr.name_th`)}
            onChange={(e) => setField({ ...field, menu_name: e.target.value })}
            value={field.menu_name}
            variant="standard"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={t(`attr.name_en`)}
            onChange={(e) => setField({ ...field, menu_name_en: e.target.value })}
            value={field.menu_name_en}
            variant="standard"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={t(`attr.sort`)}
            onChange={(e) => setField({ ...field, weight: e.target.value })}
            value={field.weight}
            variant="standard"
            fullWidth
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={field.enable === 1}
                onChange={(e) => setField({ ...field, enable: e.target.checked ? 1 : 0 })}
                inputProps={{
                  "aria-label": "enable",
                }}
              />
            }
            label="Enable"
          />
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog
        open={modalAdd}
        maxWidth="sm"
        keepMounted
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label="close"
            onClick={() => setModalAdd(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>
            <Grid item xs={12}>
              {sub ? renderInfoSub() : renderInfo()}
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          {sub ? (
            <MDButton variant="gradient" color="success" onClick={addSub}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          ) : (
            <MDButton variant="gradient" color="success" onClick={add}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderEdit = () => {
    return (
      <Dialog
        open={modalEdit}
        maxWidth="sm"
        keepMounted
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>
          {t(`element.edit`)}
          <IconButton
            aria-label="close"
            onClick={() => setModalEdit(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{sub ? renderInfoSub() : renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {sub ? (
            <MDButton variant="gradient" color="success" onClick={editSub}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          ) : (
            <MDButton variant="gradient" color="success" onClick={edit}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.tool`), t(`menu.menu`)]} />
      {renderList()}
      {renderAdd()}
      {renderEdit()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default MenuMain;
